import { useId, useState } from 'react';
import { Stack } from '@mui/material';
import { SelectInput, TextInput } from 'framework/form';
import { Alert } from 'framework/ui/mui-builder';
import type { FieldElement } from '../../format/form-definition';
import { AUTHOR_TEXTS } from '../../texts/author';
import { useBuilder } from '../context';
import { RuleExtras } from './RuleParts';
import { ruleName, validationOf } from './rules';
import { matchesPattern, PATTERN_PRESETS, patternCompiles, patternPresetOf } from '../../validations';

const NO_PATTERN = 'none';
const CUSTOM = 'custom';

interface Typed {
  text: string;
  over: string | undefined;
}

export const PatternRule = ({ field }: { field: FieldElement }) => {
  const { edit } = useBuilder();
  const pattern = validationOf(field, 'pattern');
  const stored = pattern?.regex;
  const nameId = useId();
  const [custom, setCustom] = useState(stored !== undefined && !patternPresetOf(stored));
  const [typed, setTyped] = useState<Typed | null>(null);
  const [sample, setSample] = useState('');

  const choice = custom ? CUSTOM : stored === undefined ? NO_PATTERN : (patternPresetOf(stored)?.name ?? CUSTOM);
  const text = typed && typed.over === stored ? typed.text : (stored ?? '');
  const notSaved = text !== stored && (text !== '' || stored !== undefined);

  const choose = (next: string) => {
    setTyped(null);
    setCustom(next === CUSTOM);
    if (next === NO_PATTERN) {
      edit({ kind: 'removeValidation', fieldId: field.id, validationKind: 'pattern' });
      return;
    }
    const preset = PATTERN_PRESETS.find(candidate => candidate.name === next);
    if (preset)
      edit({
        kind: 'setValidation',
        fieldId: field.id,
        validation: { ...pattern, kind: 'pattern', regex: preset.regex, message: preset.message },
      });
  };

  const type = (next: string) => {
    const saved = next !== '' && patternCompiles(next);
    setTyped({ text: next, over: saved ? next : stored });
    if (saved && next !== stored)
      edit({ kind: 'setValidation', fieldId: field.id, validation: { ...pattern, kind: 'pattern', regex: next } });
  };

  const invalid = notSaved && text !== '';
  const expressionNote = !notSaved
    ? AUTHOR_TEXTS.pattern.checkedAgainstWhole
    : invalid
      ? AUTHOR_TEXTS.pattern.invalid(stored)
      : AUTHOR_TEXTS.pattern.unfinished(stored);
  const tried = stored !== undefined && sample.trim() !== '';
  const matches = tried && matchesPattern(stored, sample);

  return (
    <Stack useFlexGap spacing={1}>
      <Stack spacing={2}>
        <SelectInput
          source={`${nameId}-pattern`}
          label={ruleName(field, 'pattern')}
          value={choice}
          onChangeCallback={values => choose(values[`${nameId}-pattern`])}
          options={[
            { value: NO_PATTERN, label: 'No pattern' },
            ...PATTERN_PRESETS.map(preset => ({ value: preset.name, label: preset.name })),
            { value: CUSTOM, label: 'Custom regular expression…' },
          ]}
          sx={{ mb: 0 }}
        />

        {choice === CUSTOM && (
          <TextInput
            source={`${nameId}-regex`}
            label="Regular expression"
            placeholder="[A-Z]{2}[0-9]{6}"
            value={text}
            errorMessages={invalid ? [expressionNote] : []}
            helpText={invalid ? undefined : expressionNote}
            onChangeCallback={values => type(values[`${nameId}-regex`])}
          />
        )}

        {stored !== undefined && (
          <TextInput
            source={`${nameId}-sample`}
            label="Try a value"
            value={sample}
            onChangeCallback={values => setSample(values[`${nameId}-sample`])}
            errorMessages={tried && !matches ? [AUTHOR_TEXTS.pattern.doesntMatch] : []}
            helpText={tried && matches ? AUTHOR_TEXTS.pattern.matches : ' '}
          />
        )}

        {pattern && !pattern.message?.trim() && (
          <Alert severity="warning" role="status">
            {AUTHOR_TEXTS.pattern.addErrorText}
          </Alert>
        )}
      </Stack>
      {pattern && <RuleExtras field={field} validation={pattern} />}
    </Stack>
  );
};
